import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney, fmtDate, fmtTime, timeAgo } from "@/lib/caesar";
import { Loading, SectionLabel } from "@/components/common";
import { MutedEmpty } from "@/components/widgets";
import { toast } from "sonner";
import { Send } from "lucide-react";

function Section({ n, title, children }) {
  return (
    <div className="bg-surface border border-edge rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <span className="font-num text-gold/50 text-lg font-bold">{n}</span>
        <span className="label-caps">{title}</span>
      </div>
      {children}
    </div>
  );
}

const Line = ({ left, right }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-edge/50 last:border-0">
    <span className="text-sm text-[#FAFAFA] truncate mr-2">{left}</span>
    {right && <span className="font-num text-xs text-[#A3A3A3] shrink-0">{right}</span>}
  </div>
);

export default function MorningBrief() {
  const [d, setD] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get("/dashboard/morning-brief"); setD(data); }, []);
  useEffect(() => { load(); }, [load]);
  const sendNow = async () => {
    try {
      const { data } = await api.post("/brief/send");
      if (data.email_sent || data.sms_sent) {
        toast.success(`Brief sent${data.email_sent ? " · email" : ""}${data.sms_sent ? " · SMS" : (data.sms_configured ? "" : " (SMS not set up yet)")}`);
      } else {
        toast.error("Delivery not configured yet");
      }
    } catch { toast.error("Could not send brief"); }
  };
  if (!d) return <Loading label="Generating the briefing…" />;
  const m = d.money;

  return (
    <div className="space-y-6">
      <div className="text-center py-4">
        <p className="label-caps text-gold">Good Morning, Commander</p>
        <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mt-2">The State of the Empire</h1>
        <p className="text-[#A3A3A3] font-num text-sm mt-2">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
        <button data-testid="brief-send-now" onClick={sendNow} className="mt-4 inline-flex items-center gap-2 px-4 h-9 rounded-lg border border-gold/40 text-gold text-sm font-semibold hover:bg-gold/10 transition-colors"><Send size={14} /> Send me this brief now</button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Section n="1" title="Money">
          <Line left="Collected this month" right={fmtMoney(m.collected_month)} />
          <Line left="Expected" right={fmtMoney(m.expected)} />
          <Line left="Overdue" right={fmtMoney(m.overdue)} />
          <Line left="Closeable now" right={fmtMoney(m.closeable)} />
        </Section>

        <Section n="2" title="Calendar">
          {d.calendar.length === 0 ? <MutedEmpty>No events today.</MutedEmpty> : d.calendar.map((e) => <Line key={e.id} left={e.title} right={fmtTime(e.time_start)} />)}
        </Section>

        <Section n="3" title="Customers">
          {d.customers.length === 0 ? <MutedEmpty>No customers need attention.</MutedEmpty> : d.customers.map((c) => <Line key={c.id} left={c.name} right={fmtMoney((c.amount_sold || 0) - (c.amount_paid || 0))} />)}
        </Section>

        <Section n="4" title="Projects">
          {d.projects.length === 0 ? <MutedEmpty>No yellow or red projects.</MutedEmpty> : d.projects.map((p) => <Line key={p.id} left={p.name} right={p.status.toUpperCase()} />)}
        </Section>

        <Section n="5" title="Relationships">
          {d.relationships.length === 0 ? <MutedEmpty>No follow-ups due.</MutedEmpty> : d.relationships.map((p) => <Line key={p.id} left={p.name} right={fmtDate(p.next_interaction)} />)}
        </Section>

        <Section n="6" title="Opportunities">
          {d.opportunities.length === 0 ? <MutedEmpty>No opportunities in motion.</MutedEmpty> : d.opportunities.map((o) => <Line key={o.id} left={o.name} right={fmtMoney(o.estimated_value)} />)}
        </Section>

        <Section n="7" title="Risks">
          {d.risks.length === 0 ? <MutedEmpty>No active risks.</MutedEmpty> : d.risks.map((a) => <Line key={a.id} left={a.problem} />)}
        </Section>

        <Section n="8" title="Waiting On">
          {d.waiting_on.length === 0 ? <MutedEmpty>Not waiting on anyone.</MutedEmpty> : d.waiting_on.map((t) => <Line key={t.id} left={t.title} right={t.owner} />)}
        </Section>

        <Section n="9" title="Wins">
          {d.wins.length === 0 ? <MutedEmpty>No recent wins.</MutedEmpty> : d.wins.map((w) => <Line key={w.id} left={w.description} right={timeAgo(w.created_at)} />)}
        </Section>
      </div>

      <div className="bg-surface border border-gold/40 rounded-xl p-5">
        <SectionLabel className="!text-gold">The Five Moves Today</SectionLabel>
        {d.caesars_five.length === 0 ? <MutedEmpty>No priority moves yet.</MutedEmpty> : (
          <div className="space-y-2">
            {d.caesars_five.map((t, i) => (
              <div key={t.id} className="flex items-start gap-3 py-2 border-b border-edge/50 last:border-0">
                <span className="font-num text-gold/40 font-bold">{i + 1}</span>
                <div><p className="text-sm font-semibold">{t.title}</p><p className="text-xs text-[#A3A3A3]">Why this matters: highest priority action{t.deadline ? ` · due ${fmtDate(t.deadline)}` : ""}.</p></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
